import dayjs from 'dayjs';
import {
  DashboardWorkerOnShiftResponse,
  DaySequence,
  DaySequenceStatus,
  Job,
  JobStatus,
  User,
} from '@core/generated/gql-types';
import { formatJobDayClockTime } from '@core/util/jobScheduleBounds.util';
import {
  formatInJobTimezone,
  getJobTimezone,
  isUnixMsTimestamp,
} from '@core/util/jobTimezone.util';
import { doesJobWorkInDateRange } from '@/modules/jobs/utils/jobFilters.utils';

const APPROVED_SHIFT_STATUSES = new Set<DaySequenceStatus>([
  DaySequenceStatus.ReadyForPay,
  DaySequenceStatus.PayProcessed,
  DaySequenceStatus.Completed,
]);

export enum ShiftCheckInStatus {
  NotCheckedIn = 'NOT_CHECKED_IN',
  Scheduled = 'SCHEDULED',
  CheckedIn = 'CHECKED_IN',
  RequiresApproval = 'REQUIRES_APPROVAL',
  Approved = 'APPROVED',
  DidNotWork = 'DID_NOT_WORK',
}

export const SHIFT_CHECK_IN_STATUS_LABEL: Record<ShiftCheckInStatus, string> = {
  [ShiftCheckInStatus.NotCheckedIn]: 'Not checked-in',
  [ShiftCheckInStatus.Scheduled]: 'Scheduled',
  [ShiftCheckInStatus.CheckedIn]: 'Checked-in',
  [ShiftCheckInStatus.RequiresApproval]: 'Requires approval',
  [ShiftCheckInStatus.Approved]: 'Approved',
  [ShiftCheckInStatus.DidNotWork]: 'Did not work',
};

export const SHIFT_CHECK_IN_STATUS_BADGE_CLASS: Record<
  ShiftCheckInStatus,
  string
> = {
  [ShiftCheckInStatus.NotCheckedIn]: 'bg-red-100 text-red-800',
  [ShiftCheckInStatus.Scheduled]: 'bg-blue-100 text-blue-800',
  [ShiftCheckInStatus.CheckedIn]: 'bg-green-100 text-green-800',
  [ShiftCheckInStatus.RequiresApproval]: 'bg-blue-100 text-blue-800',
  [ShiftCheckInStatus.Approved]: 'bg-green-100 text-green-800',
  [ShiftCheckInStatus.DidNotWork]: 'bg-yellow-100 text-yellow-800',
};

const ROSTERED_JOB_STATUSES = new Set<JobStatus>([
  JobStatus.Filled,
  JobStatus.Completed,
]);

/**
 * Today's roster from the jobs already loaded for the dashboard summary.
 * Same schedule rule as Active Candidates, limited to this calendar day.
 */
export const buildShiftsFromStores = (params: {
  tenantId: string | null | undefined;
  jobs: Job[];
  daySequences: DaySequence[];
  workers: User[];
  calendarDate: string;
}): DashboardWorkerOnShiftResponse[] => {
  const workerById = new Map(
    params.workers.map((worker) => [worker.id, worker]),
  );
  const daySequenceByJobId = new Map(
    params.daySequences
      .filter((daySequence) => daySequence.startDateISO === params.calendarDate)
      .map((daySequence) => [daySequence.jobId, daySequence]),
  );
  const calendarDay = dayjs(params.calendarDate);
  const dayStart = calendarDay.startOf('day').toDate();
  const dayEnd = calendarDay.endOf('day').toDate();

  return params.jobs
    .filter(
      (job) =>
        job.tenantId === params.tenantId &&
        job.userId != null &&
        job.itemStatus != null &&
        ROSTERED_JOB_STATUSES.has(job.itemStatus) &&
        doesJobWorkInDateRange(job, dayStart, dayEnd),
    )
    .map((job): DashboardWorkerOnShiftResponse => ({
      isoDate: params.calendarDate,
      job,
      daySequence: daySequenceByJobId.get(job.id),
      user: workerById.get(job.userId ?? ''),
    }));
};

/**
 * Rostered `HH:mm` start/end in job-local time. Prefers the day sequence, then
 * the job's daily shift times, then the time of day on the job start/end.
 */
const getScheduledClockTimes = (shift: DashboardWorkerOnShiftResponse) => ({
  start:
    formatJobDayClockTime(shift.job?.days?.startTime) ||
    formatInJobTimezone(shift.job?.startDate, shift.job),
  end:
    formatJobDayClockTime(shift.job?.days?.endTime) ||
    formatInJobTimezone(shift.job?.endDate, shift.job),
});

export const getShiftWorkerName = (shift: DashboardWorkerOnShiftResponse) =>
  `${shift.user?.firstName ?? ''} ${shift.user?.lastName ?? ''}`.trim();

export const getShiftClientId = (shift: DashboardWorkerOnShiftResponse) =>
  shift.job?.clientId ||
  shift.job?.parentId ||
  shift.daySequence?.clientId ||
  '';

export const formatShiftJobTime = (shift: DashboardWorkerOnShiftResponse) => {
  const { start, end } = getScheduledClockTimes(shift);
  if (!start && !end) return '-';
  return `${start || '-'} - ${end || '-'}`;
};

export const formatShiftCheckInTime = (
  shift: DashboardWorkerOnShiftResponse,
) => {
  const clockInLabel = formatInJobTimezone(
    shift.daySequence?.clockInTime,
    shift.job,
  );
  const clockOutLabel = formatInJobTimezone(
    shift.daySequence?.clockOutTime,
    shift.job,
  );
  if (!clockInLabel) return '-';
  return clockOutLabel ? `${clockInLabel} - ${clockOutLabel}` : clockInLabel;
};

/** Start of the shift on its rostered day, in the job site timezone. */
const getShiftStartForDay = (shift: DashboardWorkerOnShiftResponse) => {
  const { start: startTime } = getScheduledClockTimes(shift);
  if (!startTime) return null;
  return dayjs.tz(`${shift.isoDate} ${startTime}`, getJobTimezone(shift.job));
};

export const getShiftCheckInStatus = (
  shift: DashboardWorkerOnShiftResponse,
  now = dayjs(),
): ShiftCheckInStatus => {
  if (shift.daySequence?.status === DaySequenceStatus.NotWorked) {
    return ShiftCheckInStatus.DidNotWork;
  }
  const timesheetStatus = shift.daySequence?.status;
  // Checked out and waiting on a reviewer. Distinct from Approved, which is
  // only Ready for pay, Pay processed, or Completed.
  if (timesheetStatus === DaySequenceStatus.RequiresApproval) {
    return ShiftCheckInStatus.RequiresApproval;
  }
  if (timesheetStatus && APPROVED_SHIFT_STATUSES.has(timesheetStatus)) {
    return ShiftCheckInStatus.Approved;
  }
  if (isUnixMsTimestamp(shift.daySequence?.clockInTime)) {
    return ShiftCheckInStatus.CheckedIn;
  }
  const shiftStart = getShiftStartForDay(shift);
  if (shiftStart && now.isAfter(shiftStart)) {
    return ShiftCheckInStatus.NotCheckedIn;
  }
  return ShiftCheckInStatus.Scheduled;
};

/** Shifts that need attention first, then by rostered start time. */
const STATUS_SORT_ORDER: Record<ShiftCheckInStatus, number> = {
  [ShiftCheckInStatus.NotCheckedIn]: 0,
  [ShiftCheckInStatus.Scheduled]: 1,
  [ShiftCheckInStatus.CheckedIn]: 2,
  [ShiftCheckInStatus.RequiresApproval]: 3,
  [ShiftCheckInStatus.Approved]: 4,
  [ShiftCheckInStatus.DidNotWork]: 5,
};

export const sortShiftsForToday = (
  shifts: DashboardWorkerOnShiftResponse[],
  now = dayjs(),
) =>
  [...shifts].sort((left, right) => {
    const statusDifference =
      STATUS_SORT_ORDER[getShiftCheckInStatus(left, now)] -
      STATUS_SORT_ORDER[getShiftCheckInStatus(right, now)];
    if (statusDifference !== 0) return statusDifference;
    return (
      (getShiftStartForDay(left)?.valueOf() ?? Number.POSITIVE_INFINITY) -
      (getShiftStartForDay(right)?.valueOf() ?? Number.POSITIVE_INFINITY)
    );
  });
